import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import LeadTable from "./LeadTable";
import LeadPagination from "./LeadPagination";
import LeadCompainModal from "./LeadCompainModal";
import LeadCompainsDropdown from "./LeadCompainsDropdown";
import {
  addExcelDataAction,
  deleteCampaignsAction,
  fetchCampaignsAction,
  fetchLeadsAction,
  startCampaignAction,
  stopCampaignAction,
  searchLeadsAction,
} from "../../store/lead/leadThunk";
import { MdCloudUpload, MdWarning } from "react-icons/md";
import { VscSearch } from "react-icons/vsc";
import { IoMdClose } from "react-icons/io";
import {
  setSelectedCampaign,
  addCampaign,
  removeCompaign,
  startCompaign,
  stopCompaign,
  reset,
  resetLead,
  clearSearch,
} from "../../store/lead/leadSlice";
import * as XLSX from "xlsx";
import { TailSpin } from "react-loader-spinner";
import { fetchWorkersAction } from "store/worker/workerThunk";
import { getUnassignedBots } from "store/worker/workerThunk";

export default function LeadParent({ color }) {
  const [files, setFiles] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [assignedWorkers, setAssignedWorkers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isFetchingCampaigns, setIsFetchingCampaigns] = useState(false);
  const [isFetchingLeads, setIsFetchingLeads] = useState(false);
  const [isDeletingCampaign, setIsDeletingCampaign] = useState(false);
  const [isStartingCampaign, setIsStartingCampaign] = useState(false);
  const [isStoppingCampaign, setIsStoppingCampaign] = useState(false);
  const [showWarning, setShowWarning] = useState(true);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const recordsPerPage = 10;
  const dispatch = useDispatch();
  const { workers } = useSelector((state) => state.worker);

  const { leads, isLoading, error, totalRecords, totalPages, currentPage: reduxCurrentPage, campaigns, selectedCampaign, isSearchResult } =
    useSelector((state) => state.leads);

  const [isSmallScreen, setIsSmallScreen] = useState(false);
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 1000);
    };

    // Initialize the state based on the current window size
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const activeWorkers = workers.filter((worker) => worker.status === "Active");

  useEffect(() => {
    setIsFetchingCampaigns(true);
    dispatch(fetchCampaignsAction()).finally(() =>
      setIsFetchingCampaigns(false)
    );
  }, [dispatch]);

  useEffect(() => {
    if (selectedCampaign && selectedCampaign.id && !isSearchResult) {
      setIsFetchingLeads(true);
      console.log(selectedCampaign, "campaign");
      fetchLeads(selectedCampaign.id, currentPage).finally(() =>
        setIsFetchingLeads(false)
      );
    }
  }, [selectedCampaign, currentPage, isSearchResult]);

  const fetchLeads = (campaignId, page) => {
    if (!campaignId) {
      console.log("No campaign selected");
      return;
    }

    return dispatch(
      fetchLeadsAction({
        campaignId,
        page,
        onError: (error) => console.log(error,"its error"),
      })
    );
  };
  const handleDetailClick = () => {
    dispatch(fetchWorkersAction());
    dispatch(getUnassignedBots());
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!files.some((f) => f.fileName === file.name)) {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data);
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          toast.error("Please add some data in the Excel file.");
          return;
        }

        setSelectedFile(file);
        setShowWarning(false);
        setIsModalOpen(true);
        handleDetailClick();
      } else {
        toast.error("File already exists!");
      }
      e.target.value = null;
    }
  };

    const handleCampaignConfirm = async (campaignInfo) => {
    if (selectedFile) {
      setIsUploading(true);
      const formData = new FormData();
      formData.append("campaignName", campaignInfo.campaignName);
      formData.append("worker_id", campaignInfo.workerId);
      formData.append("message", campaignInfo.Message);
      formData.append("file", selectedFile);

      await dispatch(
        addExcelDataAction({
          excelData: formData,
          onSuccess: () => {
            toast.success("File processed successfully");
            setSelectedFile(null);
            setShowWarning(true);
          },
          onError: (error) => toast.error(error),
        })
      )
        .then((action) => {
          if (addExcelDataAction.fulfilled.match(action)) {
            const newFile = {
              fileName: action.payload.file_name,
              filePath: action.payload.file_path,
            };
            setFiles((prevFiles) => [...prevFiles, newFile]);

            // Dispatch addCampaign action to update campaigns in Redux store
            dispatch(
              addCampaign({
                value: action.payload.details.CompaignId,
                label: campaignInfo.campaignName,
              })
            );
            dispatch(fetchCampaignsAction());
          }
        })
        .finally(() => setIsUploading(false));
    }
  };

  const handleCampaignChange = (selected) => {
    dispatch(
      setSelectedCampaign({ id: selected.id, value: selected.label, label: selected.label })
    );
    setCurrentPage(1);
    
    // Clear search when campaign changes
    setSearchKeyword("");
    dispatch(clearSearch());
  };

  const removeFile = (index) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    toast.warning("File removed successfully!");
  };

  const handleDeleteCampaign = async (valueToDelete) => {
    setIsDeletingCampaign(true);
    const result = await dispatch(
      deleteCampaignsAction({ identifier: valueToDelete })
    );

    if (result.payload?.success) {
      toast.success("Campaign deleted successfully!");
      dispatch(removeCompaign(valueToDelete));
      dispatch(resetLead());

      dispatch(fetchCampaignsAction()); // Fetch updated campaigns list
    } else {
      toast.error(result.payload.message);
    }
    setIsDeletingCampaign(false);
  };

  const handleStartCampaign = async (valueTostart) => {
    setIsStartingCampaign(true);
    const result = await dispatch(
      startCampaignAction({ campaignId: valueTostart })
    );

    if (result.payload?.success) {
      dispatch(startCompaign(result.payload.campaign));
      dispatch(resetLead());
      dispatch(fetchCampaignsAction()); // Fetch updated campaigns list
      fetchLeads(valueTostart, currentPage); // Use valueTostart instead of selectedCampaign.id
    } else {
      console.log(result.payload.message);
    }
    setIsStartingCampaign(false);
  };

  const handleStopCampaign = async (valueTostop) => {
    setIsStoppingCampaign(true);
    const result = await dispatch(
      stopCampaignAction({ campaignId: valueTostop })
    );

    if (result.payload?.details) {
      dispatch(stopCompaign(result.payload));
      dispatch(resetLead());
      dispatch(fetchCampaignsAction());
    } else {
      toast.error(result.payload.message);
    }
    setIsStoppingCampaign(false);
  };

  // Handle page changes - the useEffect above will handle fetching data
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Search functionality
  const handleSearch = async () => {
    if (!searchKeyword.trim()) {
      toast.warning("Please enter a search keyword");
      return;
    }

    if (!selectedCampaign?.id) {
      toast.error("Please select a campaign first");
      return;
    }

    setIsSearching(true);
    try {
      await dispatch(
        searchLeadsAction({
          campaignId: selectedCampaign.id,
          name: searchKeyword.trim(),
          // page: 1,
          onSuccess: () => {
            setCurrentPage(1);
          },
          onError: (error) => {
            toast.error(error);
          },
        })
      );
    } finally {
      setIsSearching(false);
    }
  };

  const handleClearSearch = () => {
    setSearchKeyword("");
    dispatch(clearSearch());
    setCurrentPage(1);
    
    // Fetch original leads
    if (selectedCampaign?.id) {
      setIsFetchingLeads(true);
      fetchLeads(selectedCampaign.id, 1).finally(() =>
        setIsFetchingLeads(false)
      );
    }
  };

  const handleSearchInputChange = (e) => {
    setSearchKeyword(e.target.value);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="bg-white flex flex-col items-center py-3 w-full pb-5 rounded-lg">
        <form
          className="form-class w-[270px] md:h-[200px] md:w-[370px] border-2 border-dotted border-blue-500 p-5 text-center cursor-pointer"
          onClick={() => document.querySelector(".input-field").click()}
        >
          <input
            type="file"
            accept=".xlsx, .xls, .csv"
            className="input-field "
            hidden
            onChange={handleFileChange}
          />
          <MdCloudUpload color="#1475cf" size={40} />
          <p>Browse Files to Upload</p>
        </form>
        {!showWarning && selectedFile && selectedFile.name && (
          <div className="mt-4 p-3 border border-green-500 bg-green-100 text-green-700 rounded-md">
            <p>Selected File: {selectedFile.name}</p>
          </div>
        )}
        {showWarning && (
          <div className="m-[20px] p-4 border border-red-500 bg-red-100 text-red-700 rounded-md flex items-center w-[70%] mx-auto">
            <MdWarning size={24} className="mr-2" />
            <p>
              Please ensure the Excel file contains unique phone numbers that do
              not already exist in the leads table. Duplicate phone numbers will
              prevent the file from being uploaded.
            </p>
          </div>
        )}
      </div>
      <div className="rounded-lg mb-4 bg-white ">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between my-5 mx-5 gap-4">
          {/* Campaign Selection */}
          <div className="flex items-center gap-4 lg:w-1/2">
            <h1 className="font-bold text-xl whitespace-nowrap">Select Campaign:</h1>
            <div className="flex-1 min-w-0">
              {isFetchingCampaigns ? (
                <div className="flex justify-center">
                  <TailSpin color="#00BFFF" height={30} width={30} />
                </div>
              ) : (
                <LeadCompainsDropdown
                  noobcampaigns={campaigns}
                  onChange={handleCampaignChange}
                  currentCampaign={
                    selectedCampaign ? selectedCampaign.value : null
                  }
                  onDeleteCampaign={handleDeleteCampaign}
                  onStartCampaign={handleStartCampaign}
                  onStopCampaign={handleStopCampaign}
                />
              )}
            </div>
          </div>

          {/* Search Bar */}
          {selectedCampaign && (
            <div className="flex items-center gap-3 lg:w-1/2">
              <h2 className="font-bold text-xl whitespace-nowrap">Search Leads:</h2>
              <div className="flex items-center gap-2 flex-1">
                <div className="relative flex-1 max-w-sm">
                  <input
                    type="text"
                    placeholder="Search by lead name..."
                    value={searchKeyword}
                    onChange={handleSearchInputChange}
                    onKeyPress={handleSearchKeyPress}
                    className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    disabled={isSearching}
                  />
                  <VscSearch 
                    className="absolute left-3 top-[15px] transform -translate-y-1/2 text-gray-400" 
                    size={16}
                  />
                  {searchKeyword && (
                    <button
                      onClick={handleClearSearch}
                      className="absolute right-3 top-[15px] transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      type="button"
                    >
                      <IoMdClose size={16} />
                    </button>
                  )}
                </div>
                <button
                  onClick={handleSearch}
                  disabled={!searchKeyword.trim() || isSearching}
                  className={`px-4 py-2.5 rounded-lg font-medium transition-colors duration-200 text-sm whitespace-nowrap ${
                    !searchKeyword.trim() || isSearching
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-blue-500 hover:bg-blue-600 text-white"
                  }`}
                >
                  {isSearching ? "Searching..." : "Search"}
                </button>
                {isSearchResult && (
                  <button
                    onClick={handleClearSearch}
                    className="px-3 py-2.5 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors duration-200 text-sm whitespace-nowrap"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      

        {isFetchingLeads ? (
          <div className="flex justify-center my-10">
            <TailSpin color="#00BFFF" height={50} width={50} />
          </div>
        ) : (
          <>
            <LeadTable
              leads={leads}
              assignedWorkers={assignedWorkers}
              workers={workers}
              currentPage={currentPage}
              recordsPerPage={recordsPerPage}
            />
            <LeadPagination
              totalPages={totalPages}
              totalRecords={totalRecords}
              currentPage={currentPage}
              setCurrentPage={handlePageChange}
              recordsPerPage={recordsPerPage}
            />
          </>
        )}
      </div>
      <LeadCompainModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setShowWarning(true);
        }}
        onConfirm={handleCampaignConfirm}
        workers={activeWorkers}
        isUploading={isUploading}
      />
    </div>
  );
}
